import { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";

import type { TimedPoint } from "@/lib/replay-core";

export type TelemetryMetric =
  | "speed_kmh"
  | "horizontal_speed_kmh"
  | "altitude_m"
  | "relative_altitude_m"
  | "vertical_rate_mps"
  | "yaw_deg"
  | "pitch_deg"
  | "roll_deg";

const METRIC_LABELS: Record<TelemetryMetric, string> = {
  speed_kmh: "Speed (km/h)",
  horizontal_speed_kmh: "Ground speed (km/h)",
  altitude_m: "Altitude (m)",
  relative_altitude_m: "Relative altitude (m)",
  vertical_rate_mps: "Vertical rate (m/s)",
  yaw_deg: "Heading (°)",
  pitch_deg: "Pitch (°)",
  roll_deg: "Roll (°)",
};

function pick(point: TimedPoint, metric: TelemetryMetric): number | null {
  switch (metric) {
    case "speed_kmh":
      return point.speed_mps != null ? point.speed_mps * 3.6 : null;
    case "horizontal_speed_kmh":
      return point.horizontal_speed_mps != null
        ? point.horizontal_speed_mps * 3.6
        : point.speed_mps != null
          ? point.speed_mps * 3.6
          : null;
    case "altitude_m":
      return point.altitude_m;
    case "relative_altitude_m":
      return point.home_altitude_relative_m ?? null;
    case "vertical_rate_mps":
      return point.vertical_rate_mps ?? null;
    case "yaw_deg":
      return point.yaw_deg ?? null;
    case "pitch_deg":
      return point.pitch_deg ?? null;
    case "roll_deg":
      return point.roll_deg ?? null;
  }
}

interface Props {
  points: TimedPoint[];
  elapsed: number;
  onSeek?: (elapsed: number) => void;
  height?: number;
  initialMetric?: TelemetryMetric;
}

export function TelemetryMetricChart({
  points,
  elapsed,
  onSeek,
  height = 160,
  initialMetric = "speed_kmh",
}: Props) {
  const [metric, setMetric] = useState<TelemetryMetric>(initialMetric);

  const data = useMemo(
    () =>
      points
        .map((p) => {
          const v = pick(p, metric);
          return v == null ? null : ([p.elapsed_seconds, v] as [number, number]);
        })
        .filter((x): x is [number, number] => x !== null),
    [points, metric],
  );

  const option = useMemo(
    () => ({
      animation: false,
      grid: { left: 50, right: 20, top: 18, bottom: 28 },
      tooltip: {
        trigger: "axis",
        formatter: (params: unknown) => {
          const arr = params as Array<{ value: [number, number] }>;
          if (!arr?.length) return "";
          const [t, v] = arr[0].value;
          return `${t.toFixed(1)} s<br/><strong>${v.toFixed(1)}</strong>`;
        },
      },
      xAxis: {
        type: "value",
        min: points.length ? points[0].elapsed_seconds : 0,
        max: points.length ? points[points.length - 1].elapsed_seconds : 0,
        axisLabel: { color: "#A0AAB8", fontSize: 10 },
        axisLine: { lineStyle: { color: "#2A3444" } },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        scale: true,
        axisLabel: { color: "#A0AAB8", fontSize: 10 },
        axisLine: { lineStyle: { color: "#2A3444" } },
        splitLine: { lineStyle: { color: "rgba(160,170,184,0.1)" } },
      },
      series: [
        {
          type: "line",
          data,
          showSymbol: false,
          smooth: true,
          lineStyle: { color: "#FF6B1A", width: 2 },
          areaStyle: {
            color: "rgba(255,107,26,0.12)",
          },
          markLine: {
            symbol: "none",
            silent: true,
            animation: false,
            lineStyle: { color: "#E8ECF1", width: 1.5 },
            label: { show: false },
            data: [{ xAxis: elapsed }],
          },
        },
      ],
    }),
    [data, elapsed, points],
  );

  const onEvents = useMemo(
    () => ({
      click: (params: { value?: [number, number] }) => {
        if (onSeek && Array.isArray(params.value)) onSeek(params.value[0]);
      },
    }),
    [onSeek],
  );

  return (
    <div className="telemetry-timeline">
      <div className="telemetry-timeline-head">
        <span className="telemetry-timeline-title">Telemetry timeline</span>
        <label className="telemetry-metric-select">
          <span>Metric</span>
          <select value={metric} onChange={(e) => setMetric(e.target.value as TelemetryMetric)}>
            {(Object.keys(METRIC_LABELS) as TelemetryMetric[]).map((k) => (
              <option key={k} value={k}>
                {METRIC_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ReactECharts
        option={option}
        onEvents={onEvents}
        style={{ height, width: "100%" }}
        notMerge
        lazyUpdate
      />
    </div>
  );
}
